import React, { useMemo, useState, useCallback } from 'react';
import { GameState, Player, League, TeamInLeague, DisplayFixture } from '../../../domain/entities';
import { FixturesHelper } from '../../../services/FixturesHelper';
import { View } from '../../../App';
import { soundService } from '../../../services/SoundService';
import { calculateStarRating } from '../../../domain/constants';
import { MatchDetailsModal } from '../../shared/MatchDetailsModal';

const getRatingColor = (rating: number): string => {
    if (rating >= 8) return '#4caf50'; // green
    if (rating >= 7) return '#90caf9'; // light blue
    if (rating >= 6) return '#fff9c4'; // light yellow
    if (rating >= 5) return '#ffb74d'; // orange
    return '#ef5350'; // red
};

const getPositionCategory = (position: string): number => {
    if (position === 'GK') return 0;
    if (['CB', 'LB', 'RB', 'LWB', 'RWB'].includes(position)) return 1; // DEF
    if (['DM', 'CM', 'AM', 'LM', 'RM'].includes(position)) return 2; // MID
    return 3; // FWD (ST, CF, LW, RW)
};

type SortOption = 'position' | 'name' | 'played' | 'goals' | 'assists' | 'rating' | 'form' | 'matches';

export const TeamPlayedPanel: React.FC<{ gameState: GameState, pushView: (view: View) => void, isAiPunditEnabled?: boolean, showRatingsOnMatchReport?: boolean, onCacheAiMatchReport?: (fixture: DisplayFixture, report: { title: string; message: string; }) => void }> = ({ gameState, pushView, isAiPunditEnabled = false, showRatingsOnMatchReport = false, onCacheAiMatchReport = () => {} }) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [sortBy, setSortBy] = useState<SortOption>('position');
    const [selectedFixture, setSelectedFixture] = useState<DisplayFixture | null>(null);

    // Get player's team
    const playerTeam = useMemo(() => {
        for (const league of Object.values(gameState.leagues)) {
            const team = (league as any).teams.find((t: TeamInLeague) => t.name === gameState.playerTeamName);
            if (team) return team;
        }
        return null;
    }, [gameState]);

    // Calculate league average skill
    const leagueAverageSkill = useMemo(() => {
        if (!playerTeam) return 0;
        const leagueName = Object.entries(gameState.leagues).find(([_, l]) => (l as League).teams.some(t => t.name === gameState.playerTeamName))?.[0];
        if (!leagueName) return 0;
        const league = gameState.leagues[leagueName] as League;
        const allPlayers = league.teams.flatMap(t => t.squad);
        return allPlayers.length > 0 ? allPlayers.reduce((sum, p) => sum + p.skill, 0) / allPlayers.length : 0;
    }, [gameState, playerTeam]);

    // Get squad sorted by position/name and filtered by search
    const sortedSquad = useMemo(() => {
        if (!playerTeam) return [];

        // Filter by search term
        let filtered = playerTeam.squad.filter(p =>
            p.name.toLowerCase().includes(searchTerm.toLowerCase())
        );

        // Apply sorting
        const positionOrder = ['GK', 'CB', 'LB', 'RB', 'LWB', 'RWB', 'DM', 'CM', 'AM', 'LM', 'RM', 'LW', 'RW', 'ST', 'CF'];

        filtered.sort((a, b) => {
            switch (sortBy) {
                case 'name':
                    return a.name.localeCompare(b.name);
                case 'played':
                    return b.gamesPlayed - a.gamesPlayed;
                case 'goals':
                    return b.goalsScored - a.goalsScored;
                case 'assists':
                    return b.assists - a.assists;
                case 'rating':
                    const avgA = a.recentRatings && a.recentRatings.length > 0
                        ? a.recentRatings.reduce((x, y) => x + y, 0) / a.recentRatings.length
                        : 0;
                    const avgB = b.recentRatings && b.recentRatings.length > 0
                        ? b.recentRatings.reduce((x, y) => x + y, 0) / b.recentRatings.length
                        : 0;
                    return avgB - avgA;
                case 'form':
                    return b.form - a.form;
                case 'matches':
                    return b.gamesPlayed - a.gamesPlayed;
                case 'position':
                default:
                    const catA = getPositionCategory(a.position);
                    const catB = getPositionCategory(b.position);
                    if (catA !== catB) return catA - catB;
                    return a.name.localeCompare(b.name);
            }
        });

        return filtered;
    }, [playerTeam, searchTerm, sortBy]);

    const getPlayerById = useCallback((id: number): Player | undefined => {
        for (const leagueName in gameState.leagues) {
            const league = gameState.leagues[leagueName];
            for (const team of league.teams) {
                const player = team.squad.find(p => p.id === id);
                if (player) return player;
            }
        }
        return undefined;
    }, [gameState.leagues]);

    const handlePlayerClick = (player: Player) => {
        soundService.playClick();
        pushView({ type: 'playerDetail', title: `Player: ${player.name}`, playerId: player.id });
    };

    const getAvgRating = (player: Player): string => {
        if (!player.recentRatings || player.recentRatings.length === 0) return '--';
        const avg = player.recentRatings.reduce((a, b) => a + b, 0) / player.recentRatings.length;
        return avg.toFixed(1);
    };

    const getFixtureForRating = useCallback((player: Player, ratingIndex: number): DisplayFixture | null => {
        if (!playerTeam) return null;

        // Get all fixtures for player's league
        const leagueName = gameState.playerLeagueName;
        const leagueFixtures = FixturesHelper.getAllLeagueFixtures(gameState.fixtures, leagueName);

        // Find fixtures where player's team played
        const teamFixtures = leagueFixtures
            .filter(f => (f.home === gameState.playerTeamName || f.away === gameState.playerTeamName) && f.result)
            .reverse(); // Most recent first

        // The rating index corresponds to the position in recentRatings (which are stored most recent last)
        // So we need to map it correctly
        const reverseIndex = (player.recentRatings?.length || 1) - 1 - ratingIndex;

        if (reverseIndex >= 0 && reverseIndex < teamFixtures.length) {
            const fixture = teamFixtures[reverseIndex];
            return { ...fixture, type: 'league' as const, competition: leagueName };
        }

        return null;
    }, [gameState, playerTeam, gameState.playerLeagueName]);

    return (
        <>
            <div className="table-wrapper">
                {playerTeam ? (
                    <>
                        <div className="table-header-controls">
                            <div className="table-header-left">
                                <input
                                    type="text"
                                    className="table-search-input"
                                    placeholder="Search player..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                />
                            </div>
                            <div className="table-header-right">
                                <select
                                    value={sortBy}
                                    onChange={(e) => setSortBy(e.target.value as SortOption)}
                                    className="table-sort-select"
                                >
                                    <option value="position">Position</option>
                                    <option value="name">Name</option>
                                    <option value="played">Games Played</option>
                                    <option value="goals">Goals</option>
                                    <option value="assists">Assists</option>
                                    <option value="rating">Avg Rating</option>
                                    <option value="form">Form</option>
                                    <option value="matches">Matches Played</option>
                                </select>
                            </div>
                        </div>
                        <table className="table" style={{ fontSize: '0.9em' }}>
                            <thead>
                                <tr>
                                    <th>Player</th>
                                    <th>Pos</th>
                                    <th>GP</th>
                                    <th>G</th>
                                    <th>A</th>
                                    <th style={{ color: '#ffc107' }}>Y</th>
                                    <th style={{ color: '#ff4d4d' }}>R</th>
                                    <th title="Average Rating">Avg ⭐</th>
                                    <th title="Current Form">Form</th>
                                    <th title="Last 5 Ratings">L5</th>
                                </tr>
                            </thead>
                            <tbody>
                                {sortedSquad.map(player => (
                                    <tr key={player.id}>
                                        <td>
                                            <button className="link-style" onClick={() => handlePlayerClick(player)}>
                                                {player.name}
                                            </button>
                                        </td>
                                        <td>{player.position}</td>
                                        <td>{player.gamesPlayed}</td>
                                        <td>{player.goalsScored}</td>
                                        <td>{player.assists}</td>
                                        <td style={{ color: '#ffc107' }}>{player.yellowCards}</td>
                                        <td style={{ color: '#ff4d4d' }}>{player.redCards}</td>
                                        <td>{getAvgRating(player)}</td>
                                        <td style={{
                                            color: player.form > 0 ? '#4caf50' : player.form < 0 ? '#ef5350' : 'inherit',
                                            fontWeight: player.form !== 0 ? 'bold' : 'normal'
                                        }}>
                                            {player.form >= 0 ? '+' : ''}{player.form}
                                        </td>
                                        <td style={{ display: 'flex', gap: '4px', justifyContent: 'center' }}>
                                            {player.recentRatings && player.recentRatings.length > 0 ? (
                                                [...player.recentRatings].reverse().map((rating, idx) => {
                                                    const fixture = getFixtureForRating(player, idx);
                                                    return (
                                                        <button
                                                            key={idx}
                                                            onClick={() => {
                                                                if (fixture) {
                                                                    soundService.playClick();
                                                                    setSelectedFixture(fixture);
                                                                }
                                                            }}
                                                            style={{
                                                                backgroundColor: getRatingColor(rating),
                                                                color: 'white',
                                                                padding: '2px 6px',
                                                                borderRadius: '3px',
                                                                fontSize: '0.8em',
                                                                fontWeight: 'bold',
                                                                minWidth: '24px',
                                                                textAlign: 'center',
                                                                border: 'none',
                                                                cursor: fixture ? 'pointer' : 'default',
                                                                opacity: fixture ? 1 : 0.6,
                                                                transition: 'transform 0.1s'
                                                            }}
                                                            onMouseEnter={(e) => fixture && (e.currentTarget.style.transform = 'scale(1.1)')}
                                                            onMouseLeave={(e) => (e.currentTarget.style.transform = 'scale(1)')}
                                                            title={`Rating: ${rating.toFixed(1)}${fixture ? ' - Click to view match' : ''}`}
                                                        >
                                                            {rating.toFixed(1)}
                                                        </button>
                                                    );
                                                })
                                            ) : (
                                                <span style={{ opacity: 0.5 }}>--</span>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        <p style={{ fontSize: '0.8em', opacity: 0.7, marginTop: '8px', padding: '0 4px' }}>
                            P=Position, GP=Games Played, G=Goals, A=Assists, Y=Yellow Cards, R=Red Cards, Avg ⭐=Average Rating, Form=Current Form (-5 to +5), L5=Last 5 Match Ratings (click to view match)
                        </p>
                    </>
                ) : (
                    <p className="text-secondary">No team selected yet.</p>
                )}
            </div>
            {selectedFixture && (
                <MatchDetailsModal
                    isOpen={!!selectedFixture}
                    onClose={() => setSelectedFixture(null)}
                    fixture={selectedFixture}
                    getPlayerById={getPlayerById}
                    gameState={gameState}
                    pushView={pushView}
                    isAiPunditEnabled={isAiPunditEnabled}
                    showRatingsOnMatchReport={showRatingsOnMatchReport}
                    onCacheAiMatchReport={onCacheAiMatchReport}
                />
            )}
        </>
    );
};